/**
 * Pricing policies.
 *
 * Each entry here is a live disagreement between the governing specification and
 * the Excel calculators PTS runs today. Rather than pick one privately, the engine
 * implements both, defaults to the specification, and reports the alternative
 * figure alongside so the commercial difference is visible.
 *
 * A quote version records the policy set it was priced under, so publishing a
 * decision never moves a figure that has already been issued.
 */
import { z } from 'zod';
export const PricingPolicySchema = z.object({
    /** C19 / TQ02. Spec says cost x 1.10; Calc 1-2 and Calc 6 compute cost / 0.90. */
    passThroughBasis: z.enum(['markup', 'gross_margin']).default('markup'),
    passThroughRate: z.number().min(0).max(0.99).default(0.1),
    /** C18 / TQ01. Spec defines Path A as tickets x hours; Calc 1-2 adds tier overhead. */
    pathAIncludesOverhead: z.boolean().default(false),
    /** C20 / TQ03. Spec section 12 says HKD 500; Calc 5 uses CEILING(...,100). */
    projectRoundingIncrement: z.union([z.literal(100), z.literal(500)]).default(500),
    /** C21 / TQ07. Terms and rate card cap at the lower of CPI or 4%; Calc 7 uses 5%. */
    staffAnnualReviewCap: z.number().min(0).max(0.25).default(0.04),
    /** C04. Section 8.4 bands amber from 10%; section 13 requires approval below 15%. */
    hardwareApprovalThreshold: z.number().min(0).max(1).default(0.15),
    /** C13. Catalogue PRJ33 says cost plus 20%; section 04 and the master say cost / 0.8. */
    subcontractBasis: z.enum(['gross_margin', 'markup']).default('gross_margin'),
    subcontractRate: z.number().min(0).max(0.99).default(0.2),
    /** C06 / TQ04. The four automatic lines are universal in 00, absent from the audit engine. */
    auditIncludesContingency: z.boolean().default(false),
    /** Unregistered, found in Calc 3: the workbook allows PM to fall to a quarter day. */
    smallWorksPmMinimumDays: z.union([z.literal(0.25), z.literal(0.5)]).default(0.5),
    /** Standing defaults that are confirmed, exposed so the demo can show their effect. */
    hardwareMargin: z.number().min(0).max(0.99).default(0.2),
    hardwareMarginFloor: z.number().min(0).max(0.99).default(0.1),
    defaultContingency: z.number().min(0).max(1).default(0.1),
    procurementServiceFeeRate: z.number().min(0).max(1).default(0.03),
    retainerRounding: z.number().positive().default(250),
    smallWorksRounding: z.number().positive().default(100),
    auditRounding: z.number().positive().default(500),
    placedPeopleMargin: z.number().min(0).max(0.99).default(0.15),
    annualUplift: z.number().min(0).max(1).default(0.04),
    userGrowth: z.number().min(0).max(1).default(0.1),
    commercialAdjustmentLimit: z.number().min(0).max(1).default(0.1),
});
export const DEFAULT_POLICY = PricingPolicySchema.parse({});
/**
 * Drives the Configuration → Pricing policy screen. Every row is a decision Ben
 * owes somebody, phrased as the commercial question rather than the code change.
 */
export const POLICY_OPTIONS = [
    {
        key: 'passThroughBasis',
        label: 'Pass-through pricing basis',
        register: 'C19 / TQ02',
        question: 'Is the 10 per cent on pass-through a management fee added to cost, or a gross margin?',
        specValue: 'markup — cost × 1.10',
        calculatorValue: 'gross_margin — cost ÷ 0.90 (11.11 per cent)',
        effect: 'On HKD 100,000 a month of circuits, HKD 1,111 a month, about HKD 40,000 over a three-year contract.',
    },
    {
        key: 'pathAIncludesOverhead',
        label: 'Retainer Path A overhead',
        register: 'C18 / TQ01',
        question: 'When a client supplies real ticket history, does the tier base overhead still apply on top?',
        specValue: 'false — known tickets × known hours only',
        calculatorValue: 'true — adds the tier base overhead hours',
        effect: 'Adds 2 to 4 hours a month at the tier rate, roughly HKD 900 to 1,800 a month.',
    },
    {
        key: 'projectRoundingIncrement',
        label: 'Project fee rounding',
        register: 'C20 / TQ03',
        question: 'Do project net fees round up to HKD 500 or HKD 100?',
        specValue: '500',
        calculatorValue: '100',
        effect: 'Up to HKD 400 per project quote, and it changes every boundary test.',
    },
    {
        key: 'staffAnnualReviewCap',
        label: 'Contract staff annual review cap',
        register: 'C21 / TQ07',
        question: 'Does contract staffing get an exception to the lower-of-CPI-or-4-per-cent cap?',
        specValue: '0.04',
        calculatorValue: '0.05',
        effect: 'One percentage point a year compounding on every placement.',
    },
    {
        key: 'hardwareApprovalThreshold',
        label: 'Hardware margin approval threshold',
        register: 'C04',
        question: 'Below what hardware margin does a quote need an approver?',
        specValue: '0.15 — section 13 sign-off matrix',
        calculatorValue: '0.10 — section 8.4 red band',
        effect: 'Decides whether the 10 to 15 per cent band is self-issuable.',
    },
    {
        key: 'subcontractBasis',
        label: 'Subcontract pricing basis',
        register: 'C13',
        question: 'Is subcontract 20 per cent a gross margin or a markup?',
        specValue: 'gross_margin — cost ÷ 0.80',
        calculatorValue: 'markup — cost × 1.20',
        effect: 'On HKD 100,000 of cabling, HKD 5,000 of margin.',
    },
    {
        key: 'auditIncludesContingency',
        label: 'Contingency on audits',
        register: 'C06 / TQ04',
        question: 'Do the four automatic lines including contingency apply to audit fees?',
        specValue: 'false — Calc 4 has no contingency line',
        calculatorValue: 'true — rule 1 says every job',
        effect: '10 per cent on every audit fee.',
    },
    {
        key: 'smallWorksPmMinimumDays',
        label: 'Small works PM minimum',
        register: 'Unregistered — found in Calc 3',
        question: 'What is the floor on project management for a small works job?',
        specValue: '0.5 day',
        calculatorValue: '0.25 day',
        effect: 'HKD 1,250 on every small works quote under one elapsed week.',
    },
];
/** A stable fingerprint of the policy set, recorded on every quote version. */
export function policyFingerprint(policy) {
    const ordered = Object.keys(policy)
        .sort()
        .map((k) => `${k}=${String(policy[k])}`)
        .join('|');
    return ordered;
}
//# sourceMappingURL=policy.js.map